/**
 * The one phone-number rule, shared by every field and form on the frontend.
 *
 * It is deliberately loose enough to fit the whole world: after stripping everything that is
 * not a digit or a "+", a phone number is an optional leading "+" followed by 7 to 15 digits
 * (15 being E.164's hard maximum). It knows nothing about countries, area codes or trunk
 * prefixes — guessing at those is what used to corrupt non-US numbers, and the country-aware
 * path is parked on the backend behind PHONE_NORMALIZE. The job here is only to keep "N/A",
 * "call the shul" and a stray "12" out of the phone column.
 *
 * This mirrors `App\Rules\PhoneNumber` on the backend, character for character. If one side
 * changes, change the other — the API rejects what this accepts and vice versa.
 */

/** Optional leading "+", then 7-15 digits. */
export const PHONE_PATTERN = /^\+?[0-9]{7,15}$/;

export const PHONE_MIN_DIGITS = 7;
export const PHONE_MAX_DIGITS = 15;

/**
 * Strip everything that is not a digit or a "+". Spaces, dashes, brackets, dots and the
 * non-breaking spaces that come out of a spreadsheet all disappear; nothing is added.
 */
export const cleanPhone = (raw: string | null | undefined): string =>
  (raw ?? "").replace(/[^0-9+]/g, "");

/**
 * Does the value look like a phone number?
 *
 * A BLANK field is valid — presence is a separate question and most phone fields are
 * optional. Note the distinction: blank means the user typed nothing. "N/A", "---" and
 * "call the shul" are not blank; they clean away to nothing, and that is an error, not an
 * empty field. Testing the cleaned value for emptiness would wave all of them through, which
 * is exactly the garbage this rule exists to stop.
 *
 * A "+" anywhere but the front fails rather than being silently repaired: "1+2025550123" is
 * a typo, and quietly fixing a typo in a phone number is how a contact becomes unreachable
 * without anyone noticing.
 */
export const isValidPhone = (raw: string | null | undefined): boolean => {
  if ((raw ?? "").trim() === "") return true;

  return PHONE_PATTERN.test(cleanPhone(raw));
};
